import { getStatusOptions } from '@/components/deal/status/deal-status-selector';
import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import { DEAL_STATUS_LABEL } from '@/lib/constants';
import { cn, pluralize } from '@/lib/utils';
import type { Status } from '@/models/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { StatusFilters } from '../types';

const STATUS_OPTIONS_MAP = getStatusOptions({ className: 'size-4' });

const StatusFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: statusFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: StatusFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return STATUS_OPTIONS_MAP.map((option) => {
      const count = getCount('status', option.value as Status);

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            {option.icon}
            {option.label}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: option.value,
        icon: (
          <div className="size-4">
            <Checkbox checked={statusFilters[option.value as Status]} />
          </div>
        ),
      };
    });
  }, [statusFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={`${DEAL_STATUS_LABEL}...`}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default StatusFiltersComboBoxPopover;
